import { useEffect, useRef, useState, useCallback } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import { useWorkspaceStore, getAllPanes } from '@/stores/workspace-store';
import { useSettingsStore } from '@/stores/settings-store';
import { useAppStore } from '@/stores/app-store';
import { TERMINAL_THEMES } from '@/config/themes';
import type { TerminalTab } from '@/types';

type TerminalState = 'connecting' | 'open' | 'disconnected' | 'closed' | 'error';


interface TerminalViewProps {
  tab: TerminalTab;
}

export function TerminalView({ tab }: TerminalViewProps) {
  const updateTab = useWorkspaceStore((s) => s.updateTab);
  const [state, setState] = useState<TerminalState>('connecting');
  const [bgColor, setBgColor] = useState('#0e1015');

  const containerRef = useRef<HTMLDivElement>(null);
  const terminalRef = useRef<Terminal | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const resizeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stateRef = useRef<TerminalState>(state);
  stateRef.current = state;
  const sessionIdRef = useRef<string | undefined>(tab.sessionId);
  sessionIdRef.current = tab.sessionId;
  // Whether the active session is backed by tmux. Set on 'opened' or
  // 'reconnected'. Wheel-scroll only sends WS scroll messages when true.
  const persistentRef = useRef<boolean>(false);
  // Cleanup function for wheel listener; replaced on each connect().
  const wheelCleanupRef = useRef<(() => void) | null>(null);

  const connect = useCallback(() => {
    if (!containerRef.current) return;

    // Clean up existing
    wsRef.current?.close();
    wsRef.current = null;
    terminalRef.current?.dispose();
    terminalRef.current = null;
    wheelCleanupRef.current?.();
    wheelCleanupRef.current = null;

    setState('connecting');

    // Read terminal settings from store
    const ss = useSettingsStore.getState();
    const termFontSize = ss.getNumber('terminal.fontSize', 13);
    const termFontFamily = ss.get('terminal.fontFamily', "'JetBrains Mono', monospace");
    const termCursorBlink = ss.getBool('terminal.cursorBlink', true);
    const termScrollback = ss.getNumber('terminal.scrollback', 10000);
    const currentAppTheme = useAppStore.getState().theme;
    const termThemeKey = currentAppTheme === 'dark' ? 'terminal.theme.dark' : 'terminal.theme.light';
    const termThemeDefault = currentAppTheme === 'dark' ? 'dbgo-dark' : 'dbgo-light';
    const termThemeId = ss.get(termThemeKey, termThemeDefault);
    const termTheme = TERMINAL_THEMES.find(t => t.id === termThemeId) ?? TERMINAL_THEMES[0];
    setBgColor(termTheme.colors.background);

    const terminal = new Terminal({
      theme: termTheme.colors,
      cursorBlink: termCursorBlink,
      fontSize: termFontSize,
      fontFamily: termFontFamily,
      scrollback: termScrollback,
    });

    const fitAddon = new FitAddon();
    terminal.loadAddon(fitAddon);
    terminal.loadAddon(new WebLinksAddon());
    terminal.open(containerRef.current);
    fitAddon.fit();

    terminalRef.current = terminal;
    fitAddonRef.current = fitAddon;

    const token = localStorage.getItem('dbgo_token');
    if (!token) {
      terminal.writeln('\r\n\x1b[31mError: Not authenticated\x1b[0m');
      setState('error');
      return;
    }

    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${location.host}/ws/terminal?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.binaryType = 'arraybuffer';

    ws.onopen = () => {
      const sid = sessionIdRef.current;
      if (sid) {
        // Try fast resume first (WS reattach within grace period)
        terminal.writeln('\x1b[2m Resuming session...\x1b[0m');
        ws.send(JSON.stringify({
          type: 'resume',
          sessionId: sid,
          cols: terminal.cols,
          rows: terminal.rows,
        }));
      } else {
        // Fresh open
        ws.send(JSON.stringify({
          type: 'open',
          tunnelId: tab.tunnelId,
          cols: terminal.cols,
          rows: terminal.rows,
        }));
      }
    };

    ws.onmessage = (event) => {
      if (event.data instanceof ArrayBuffer) {
        terminal.write(new Uint8Array(event.data));
        return;
      }

      if (typeof event.data !== 'string') return;

      try {
        const msg = JSON.parse(event.data) as {
          type: string;
          sessionId?: string;
          message?: string;
          persistent?: boolean;
          code?: number;
        };

        switch (msg.type) {
          case 'opened':
            updateTab(tab.id, { sessionId: msg.sessionId } as Partial<TerminalTab>);
            sessionIdRef.current = msg.sessionId;
            persistentRef.current = !!msg.persistent;
            if (msg.persistent) {
              terminal.writeln('\x1b[2m tmux session active — survives reconnects\x1b[0m');
            }
            setState('open');
            break;

          case 'resumed':
            persistentRef.current = true; // resume only happens for tmux sessions
            setState('open');
            break;

          case 'reconnected':
            persistentRef.current = true;
            terminal.writeln('\x1b[2m Session reconnected via tmux\x1b[0m');
            setState('open');
            break;

          case 'exit':
            terminal.writeln(`\r\n\x1b[33mSession ended (exit code ${msg.code ?? 0}). Press any key to start a new session.\x1b[0m`);
            setState('closed');
            break;

          case 'error':
            // If resume failed, try reconnect via tmux
            if (sessionIdRef.current && msg.message?.includes('Session not found')) {
              terminal.writeln('\x1b[2m Reconnecting via tmux...\x1b[0m');
              ws.send(JSON.stringify({
                type: 'reconnect',
                sessionId: sessionIdRef.current,
                tunnelId: tab.tunnelId,
                cols: terminal.cols,
                rows: terminal.rows,
              }));
              return;
            }
            // If reconnect also failed (tmux session expired), offer fresh start
            if (sessionIdRef.current && msg.message?.includes('expired')) {
              terminal.writeln(`\r\n\x1b[31m${msg.message}\x1b[0m`);
              terminal.writeln('\x1b[33mPress any key to start a new session.\x1b[0m');
              sessionIdRef.current = undefined;
              updateTab(tab.id, { sessionId: undefined } as Partial<TerminalTab>);
              setState('closed');
              return;
            }
            terminal.writeln(`\r\n\x1b[31mError: ${msg.message}\x1b[0m`);
            setState('error');
            break;
        }
      } catch {
        terminal.write(event.data);
      }
    };

    ws.onclose = () => {
      const s = stateRef.current;
      if (s !== 'closed' && s !== 'error') {
        setState('disconnected');
      }
    };

    ws.onerror = () => {
      if (stateRef.current !== 'closed') {
        setState('error');
      }
    };

    // Terminal input → WS
    terminal.onData((data) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(data);
      }
    });

    // Keypress in closed/error → start new session
    terminal.onKey(() => {
      const s = stateRef.current;
      if (s === 'closed' || s === 'error') {
        sessionIdRef.current = undefined;
        updateTab(tab.id, { sessionId: undefined } as Partial<TerminalTab>);
        setTimeout(() => connect(), 0);
      }
    });

    // Wheel scrolling under tmux. tmux runs on the alternate screen, which has
    // no xterm-side scrollback to scroll — so we send a 'scroll' control
    // message that the server translates into `tmux send-keys -X scroll-up/
    // scroll-down`. Works regardless of tmux mouse-mode config or the user's
    // ~/.tmux.conf. For non-persistent sessions the server no-ops, leaving
    // xterm's own wheel handling intact.
    let wheelAccum = 0;
    const onWheel = (e: WheelEvent) => {
      if (!persistentRef.current) return;
      // Convert deltaY into row units. Browsers report deltaY in pixels (or
      // lines if deltaMode=1). Divide pixel deltas by ~16 to get lines.
      const lineDelta = e.deltaMode === 1 ? e.deltaY : e.deltaY / 16;
      wheelAccum += lineDelta;
      const lines = Math.trunc(wheelAccum);
      if (lines === 0) return;
      wheelAccum -= lines;
      const ws = wsRef.current;
      if (ws?.readyState !== WebSocket.OPEN) return;
      ws.send(JSON.stringify({
        type: 'scroll',
        direction: lines < 0 ? 'up' : 'down',
        lines: Math.min(20, Math.abs(lines)),
      }));
      e.preventDefault();
    };
    if (containerRef.current) {
      containerRef.current.addEventListener('wheel', onWheel, { passive: false });
      wheelCleanupRef.current = () => {
        containerRef.current?.removeEventListener('wheel', onWheel);
      };
    }
  }, [tab.id, tab.tunnelId, updateTab]);

  // Mount
  useEffect(() => {
    connect();

    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(() => {
      if (resizeTimer.current) clearTimeout(resizeTimer.current);
      resizeTimer.current = setTimeout(() => {
        const fitAddon = fitAddonRef.current;
        const terminal = terminalRef.current;
        if (!fitAddon || !terminal) return;
        fitAddon.fit();
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'resize',
            cols: terminal.cols,
            rows: terminal.rows,
          }));
        }
      }, 100);
    });
    resizeObserver.observe(container);

    return () => {
      // Check if tab was closed (not just dragged)
      const allPanes = getAllPanes(useWorkspaceStore.getState().root);
      const tabStillExists = allPanes.some((p) => p.tabs.some((t) => t.id === tab.id));
      if (!tabStillExists && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'close' }));
      }
      wsRef.current?.close();
      wsRef.current = null;
      terminalRef.current?.dispose();
      terminalRef.current = null;
      fitAddonRef.current = null;
      resizeObserver.disconnect();
      wheelCleanupRef.current?.();
      wheelCleanupRef.current = null;
      if (resizeTimer.current) clearTimeout(resizeTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab.id]);

  const reconnect = useCallback(() => {
    sessionIdRef.current = undefined;
    updateTab(tab.id, { sessionId: undefined } as Partial<TerminalTab>);
    connect();
  }, [tab.id, updateTab, connect]);

  return (
    <div className="h-full w-full relative p-2" style={{ backgroundColor: bgColor }}>
      <div ref={containerRef} className="h-full w-full overflow-hidden" />
      {state === 'connecting' && (
        <div className="absolute inset-0 flex items-center justify-center bg-dbgo-root/80 z-10">
          <span className="text-text-muted text-sm">Connecting...</span>
        </div>
      )}
      {state === 'disconnected' && (
        <div className="absolute inset-0 flex items-center justify-center bg-dbgo-root/80 z-10">
          <div className="text-center">
            <span className="text-accent-yellow text-sm block mb-2">Disconnected</span>
            <button onClick={reconnect} className="text-xs text-accent-blue hover:underline">
              Reconnect
            </button>
          </div>
        </div>
      )}
      {state === 'error' && (
        <div className="absolute inset-0 flex items-center justify-center bg-dbgo-root/80 z-10">
          <div className="text-center">
            <span className="text-accent-red text-sm block mb-2">Connection failed</span>
            <button onClick={reconnect} className="text-xs text-accent-blue hover:underline">
              Retry
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
